import React, { Fragment, useRef, useContext } from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SearchIcon,
    PlusIcon,
    EyeIcon,
    EyeOffIcon,
    TrashIcon,
    SupportIcon,
} from "@heroicons/react/outline";
import { Link, useHistory, useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import { TagListContext } from ".";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { apiPut, apiDel, messageFromException } from "helpers/Request";
import { useContextualHelp } from "hooks/Help";
import SecondaryButton from "components/Buttons/SecondaryButton";
import DangerButton from "components/Buttons/DangerButton";

export default function TagListToolbar(props) {
    const inputRef = useRef();
    const history = useHistory();
    const location = useLocation();

    const auth = useAuth();

    const contextualHelp = useContextualHelp();

    const { selectedItems, load } = useContext(TagListContext);

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && val === null) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Tags",
            children: (
                <Fragment>
                    <div className="mb-4">
                        <strong>
                            It is recommended that you disable tags instead of
                            deleting them.
                        </strong>{" "}
                        Deleting tags can have unintended consequences. Disable
                        a tag to keep it from appearing in search results.
                    </div>
                    <div>Are you sure you want to delete these tags?</div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => handleDelete()}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const id = toast.loading("Deleting tag...");
        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/tag/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Tags deleted", { id });
                modal.dismiss();
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error Deleting Tag",
                    children: messageFromException(err),
                });
            });
    };

    const onRequestUpdate = (data) => {
        const id = toast.loading("Updating tags...");

        const promises = selectedItems.map((uuid) =>
            apiPut(`/v4/tag/${uuid}`, auth.publication, data)
        );
        Promise.all(promises)
            .then(() => {
                toast.success("Tags updated", { id });
                load();
            })
            .catch((err) => {
                toast.dismiss(id);
                modal.error({
                    title: "Error Updating Tags",
                    children: messageFromException(err),
                });
            });
    };

    const onKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        onSearchClick();
    };
    const onSearchClick = () => {
        const val = inputRef.current.value;
        if (!val || !val.length) {
            updateParams("keyword", null);
        } else {
            updateParams("keyword", val);
        }
    };

    return (
        <Toolbar>
            <ToolbarGroup>
                <input
                    type="text"
                    ref={inputRef}
                    className="focus:outline-none relative mr-2 flex items-center rounded-md border border-gray-300 bg-white py-0 px-2 text-xs placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 sm:w-auto"
                    placeholder="Search Tags"
                    onKeyDown={onKeyDown}
                />
                <ToolbarButton icon={<SearchIcon />} onClick={onSearchClick} />
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton icon={<PlusIcon />} as={Link} to="/app/tag/new">
                    <span className="hidden sm:inline">Add</span>
                </ToolbarButton>
                <ToolbarButton
                    icon={<EyeIcon />}
                    disabled={!selectedItems.length}
                    onClick={() => onRequestUpdate({ status: 1 })}
                >
                    Activate
                </ToolbarButton>
                <ToolbarButton
                    icon={<EyeOffIcon />}
                    disabled={!selectedItems.length}
                    onClick={() => onRequestUpdate({ status: 0 })}
                >
                    Deactivate
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={onRequestDelete}
                    disabled={!selectedItems.length}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    className="text-green-600"
                    onClick={() =>
                        contextualHelp.showContextualHelp("/tag/index.html")
                    }
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
